import * as THREE from 'three';
import { hexDist, hexToWorld, type BattleState, type Structure } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';

/**
 * Beside (bet 7, docs/SAGAS.md): Hilde's towers stand in pairs round the node and shoot only with a hero beside them
 * (`rules.beside`, the sim's `manned`). Round each tower a ring on the ground at that reach: dim and cold while nobody
 * stands in it, lit forge-orange with a soft pool when a hero does; the manned tower's top glows like a forge mouth.
 * Between each pair, Hilde's iron plaque on a post (her mark: these are hers).
 *
 * Draw calls: the plaques (one merged mesh), a ring and a pool per tower (six towers: twelve small meshes, their own
 * materials so each lights alone), a glow layer. No lights.
 */

const IDLE = new THREE.Color('#9aa6b4');
const LIT = new THREE.Color('#ff7a2a');
const LIT_CORE = new THREE.Color('#ffd2a8');
const IRON = '#2f3137';
const IRON_EDGE = '#5a5e68';
const ENAMEL = '#b8402c';
/** The tower's top, over the ground (world units), for its glow. */
const TOP = 1.45;

interface Ring {
  id: number;
  p: THREE.Vector3;
  ring: THREE.Mesh;
  pool: THREE.Mesh;
  ringMat: THREE.MeshBasicMaterial;
  poolMat: THREE.MeshBasicMaterial;
  /** How lit it is now (eases toward manned or not). */
  lit: number;
  was: boolean;
}

export class BesideLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(32);
  private rings: Ring[] = [];
  private now = 0;
  private last = 0;
  private v = new THREE.Vector3();

  constructor(
    private ctx: OmenCtx,
    towers: Structure[],
    private reach: number,
  ) {
    this.group.name = 'beside-rings';
    const r = (reach / 1000) * WORLD_PER_HEX;
    const ringGeo = new THREE.RingGeometry(r * 0.93, r, 64).rotateX(-Math.PI / 2);
    const poolGeo = new THREE.CircleGeometry(r * 0.93, 48).rotateX(-Math.PI / 2);
    this.geos.push(ringGeo, poolGeo);
    for (const st of towers) {
      const c = hexToWorld(st.q, st.r);
      const p = ctx.at(c.x, c.y, 0.06);
      const ringMat = new THREE.MeshBasicMaterial({ color: IDLE.clone(), transparent: true, opacity: 0.35, depthWrite: false, toneMapped: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
      const poolMat = new THREE.MeshBasicMaterial({ color: LIT.clone(), transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
      this.mats.push(ringMat, poolMat);
      const ring = new THREE.Mesh(ringGeo, ringMat);
      const pool = new THREE.Mesh(poolGeo, poolMat);
      for (const m of [ring, pool]) {
        m.position.copy(p);
        m.renderOrder = 2;
        this.group.add(m);
      }
      pool.position.y -= 0.005;
      this.rings.push({ id: st.id, p, ring, pool, ringMat, poolMat, lit: 0, was: false });
    }

    // Hilde's plaques: one per pair, on a post just outside the pair (away from the node).
    const kit = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 6);
    // The pairs: each tower with its neighbour (the sim sets them side by side).
    const pairs: [number, number][] = [];
    const used = new Set<number>();
    towers.forEach((t, i) => {
      if (used.has(i)) return;
      const j = towers.findIndex((o, k) => k !== i && !used.has(k) && hexDist(o, t) === 1);
      if (j < 0) return;
      used.add(i);
      used.add(j);
      pairs.push([i, j]);
    });
    for (const [i, j] of pairs) {
      const a = this.rings[i] as Ring;
      const b = this.rings[j] as Ring;
      const mx = (a.p.x + b.p.x) / 2;
      const mz = (a.p.z + b.p.z) / 2;
      const len = Math.hypot(mx, mz) || 1;
      const x = mx + (mx / len) * 0.55;
      const z = mz + (mz / len) * 0.55;
      const g = ctx.ground(x, z);
      const yaw = Math.atan2(mx, mz);
      kit.add(cyl, IRON, place(x, g + 0.45, z, 0, new THREE.Vector3(0.08, 0.9, 0.08)));
      kit.add(box, IRON_EDGE, place(x, g + 0.86, z, yaw, new THREE.Vector3(0.52, 0.38, 0.05)));
      kit.add(box, IRON, place(x, g + 0.86, z, yaw, new THREE.Vector3(0.46, 0.32, 0.06)));
      // Her mark: a hammer over an anvil, in red enamel.
      const fwd = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw)).multiplyScalar(0.04);
      kit.add(box, ENAMEL, place(x + fwd.x, g + 0.79, z + fwd.z, yaw, new THREE.Vector3(0.26, 0.06, 0.02)));
      kit.add(box, ENAMEL, place(x + fwd.x, g + 0.89, z + fwd.z, yaw, new THREE.Vector3(0.06, 0.15, 0.02)));
      kit.add(box, ENAMEL, place(x + fwd.x, g + 0.96, z + fwd.z, yaw, new THREE.Vector3(0.2, 0.055, 0.02)));
    }
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.6, metalness: 0.3, flatShading: true });
    this.mats.push(mat);
    const plaques = kit.build(mat, 'beside-plaques');
    if (plaques) {
      this.geos.push(plaques.geometry);
      this.group.add(plaques);
    }
    box.dispose();
    cyl.dispose();
    this.group.add(this.glow.mesh);
    this.group.updateMatrixWorld(true);
  }

  /** Every redraw: each tower's ring and pool, lit while a hero stands by it, and the manned towers' tops. */
  draw(s: BattleState): void {
    const dt = Math.min(0.1, Math.max(0, this.now - this.last));
    this.last = this.now;
    this.glow.begin();
    for (const r of this.rings) {
      const st = s.structures.find((x) => x.id === r.id);
      if (!st || st.destroyed) {
        r.ring.visible = false;
        r.pool.visible = false;
        continue;
      }
      const c = hexToWorld(st.q, st.r);
      let manned = false;
      for (const h of s.heroes) {
        if (h.downTicks > 0) continue;
        const dx = h.x - c.x;
        const dy = h.y - c.y;
        if (dx * dx + dy * dy <= this.reach * this.reach) {
          manned = true;
          break;
        }
      }
      if (manned && !r.was) {
        // Taken up: a warm ring snaps out under it.
        this.ctx.vfx.ring(this.v.set(r.p.x, r.p.y + 0.02, r.p.z), 0.3, 1.4, '#ff8a4a', 12, 0.7);
      }
      r.was = manned;
      r.lit += ((manned ? 1 : 0) - r.lit) * Math.min(1, dt * 8);
      const pulse = 0.5 + 0.5 * Math.sin(this.now * 3 + r.id);
      r.ringMat.color.copy(IDLE).lerp(LIT, r.lit);
      r.ringMat.opacity = 0.42 + 0.4 * r.lit + 0.12 * r.lit * pulse;
      r.poolMat.opacity = 0.1 * r.lit;
      r.pool.visible = r.lit > 0.02;
      if (r.lit > 0.02) {
        const f = r.lit * (0.85 + 0.15 * Math.sin(this.now * 7 + r.id * 1.7));
        this.glow.add(r.p.x, r.p.y + TOP, r.p.z, LIT_CORE, 0.45, 0.85 * f);
        this.glow.add(r.p.x, r.p.y + TOP, r.p.z, LIT, 1.3, 0.45 * f);
        this.glow.add(r.p.x, r.p.y + 0.1, r.p.z, LIT, 1.6, 0.12 * f);
      }
    }
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
